import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import en from '../i18n/translations/en';
import fr from '../i18n/translations/fr';
import es from '../i18n/translations/es';
import ar from '../i18n/translations/ar';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
];

const TRANSLATIONS = {
  en,
  fr,
  es,
  ar,
};

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key, fallback) => fallback || key,
  languages: SUPPORTED_LANGUAGES,
  currentLanguage: SUPPORTED_LANGUAGES[0],
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('nb_language');
    if (saved && TRANSLATIONS[saved]) return saved;
    const browserLang = (navigator.language || navigator.userLanguage || '').substring(0, 2).toLowerCase();
    if (TRANSLATIONS[browserLang]) return browserLang;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('nb_language', language);
    document.documentElement.setAttribute('lang', language);
    const langObj = SUPPORTED_LANGUAGES.find(l => l.code === language);
    if (langObj?.dir === 'rtl') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [language]);

  const setLanguage = useCallback((newLang) => {
    if (TRANSLATIONS[newLang]) {
      setLanguageState(newLang);
    }
  }, []);

  const t = useMemo(() => {
    return (keyPath, fallback, params = {}) => {
      if (!keyPath) return fallback || '';

      let fb = fallback;
      let interpolateParams = params;
      if (typeof fallback === 'object' && fallback !== null && Object.keys(params).length === 0) {
        interpolateParams = fallback;
        fb = undefined;
      }

      const resolve = (obj, path) => {
        if (!obj) return null;
        // 1. Direct flat key lookup
        if (obj[path] !== undefined && typeof obj[path] === 'string') return obj[path];
        // 2. Dot-notation lookup
        if (typeof path === 'string' && path.includes('.')) {
          const parts = path.split('.');
          let cur = obj;
          for (const p of parts) {
            if (!cur || typeof cur !== 'object') return null;
            cur = cur[p];
          }
          if (typeof cur === 'string') return cur;
        }
        return null;
      };

      // 1. Try active language
      let val = resolve(TRANSLATIONS[language], keyPath);

      // 2. Fallback to English
      if (val === null || val === undefined) {
        val = resolve(TRANSLATIONS.en, keyPath);
      }

      // 3. Fallback to fallback parameter or keyPath
      if (val === null || val === undefined) {
        val = fb !== undefined ? fb : keyPath;
      }

      if (typeof val !== 'string') return String(val);

      // Interpolate parameters {param}
      for (const [pKey, pVal] of Object.entries(interpolateParams)) {
        val = val.replace(new RegExp(`\\{${pKey}\\}`, 'g'), String(pVal));
      }

      return val;
    };
  }, [language]);

  const currentLanguageObj = useMemo(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  }, [language]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      languages: SUPPORTED_LANGUAGES,
      currentLanguage: currentLanguageObj
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
